/**
 * Main site JavaScript file
 *
 * This is the entry point for the application's JavaScript modules.
 * It imports all modules statically and initializes them when the DOM is ready.
 * Used in both development and production environments.
 */

// Import core modules
import { CONFIG, ENV } from './core/config.js';
import eventBus, { EVENTS } from './core/event-bus.js';
import errorHandler, {
    logError,
    logWarning,
    logInfo,
    logDebug,
    logEmoji,
    log,
    safeExecute,
    safeExecuteAsync,
    safeDOM,
    safeAjax
} from './core/error-handler.js';
import { debounce } from './core/utils.js';
import { BaseSessionStorageManager } from './core/session-storage-utils.js';
// Import grid modules
import { GridManager, GridManagerAPI } from './modules/grid/index.js';

// Import application modules
import * as ToastrConfig from './modules/toastr-config.js';
import * as Sidebar from './modules/sidebar.js';
import * as Cookies from './modules/cookies.js';
import * as KendoConfig from './modules/kendo-config.js';
import * as KendoHelpers from './modules/kendo-helpers.js';
import * as Result from './modules/result.js';
import * as AntiForgery from './modules/antiforgery.js';
import * as Files from './modules/files.js';
import * as FilterComponents from './modules/filter/components.js';
import { preloadFilterValues } from './modules/filter/values.js';
import './modules/filter/values.js'; // Auto-initializes global getFilterValues function
import * as Modal from './modules/modal/modal.js';
import * as Bootstrap from './modules/bootstrap.js';
import { showConfirmationDialog, showWarningConfirmationDialog } from './modules/confirmation-dialog.js';
import { init as initMaximize } from './modules/maximize.js';

// Bundle all modules for initialization
const modules = {
    ToastrConfig,
    Sidebar,
    Cookies,
    KendoConfig,
    KendoHelpers,
    Result,
    AntiForgery,
    Files,
    FilterComponents,
    GridManager,
    GridManagerAPI,
    Modal,
    Bootstrap
};

/**
 * Setup application-wide event listeners
 * @param {Object} modules - Object containing all required modules
 */
async function setupApplicationEventListeners() {
    // Listen for filter changes
    eventBus.on(
        EVENTS.FILTER_CHANGED,
        debounce(() => {
            // Filter changed - handled by components
        }, CONFIG.FILTER.TEXT_INPUT.DEBOUNCE_DELAY)
    );

    // Listen for filter manager events
    eventBus.on(EVENTS.FILTER_MANAGER_READY, () => {
        // FilterManager ready
    });

    eventBus.on(EVENTS.FILTER_MANAGER_ERROR, data => {
        logError(new Error('FilterManager error'), { data });
    });

    // Listen for grid events
    eventBus.on(EVENTS.GRID_REFRESHED, () => {
        // Grid refreshed
    });

    // Listen for application errors
    eventBus.on(EVENTS.APP_ERROR, errorInfo => {
        // Just log to console to avoid infinite loop with logError
        // eslint-disable-next-line no-console
        console.error('Application Error:', errorInfo);
    });

    // Clear persisted UI state on logout/session expiration
    const clearPersistedState = () => {
        try {
            // Clear all session storage instances at once using the registry pattern
            BaseSessionStorageManager.clearAllInstances();
            logDebug('Cleared all session storage instances', {
                instanceCount: BaseSessionStorageManager.getInstanceCount(),
                prefixes: BaseSessionStorageManager.getAllPrefixes()
            });
        } catch (error) {
            logError(error);
        }
    };

    eventBus.on(EVENTS.USER_LOGGED_OUT, clearPersistedState);
    eventBus.on(EVENTS.SESSION_EXPIRED, clearPersistedState);

    // Emit logout before navigation when user triggers logout via link or form
    $(document).on('click', 'a[href*="/Identity/Account/Logout"], a[data-logout="true"]', () => {
        eventBus.emit(EVENTS.USER_LOGGED_OUT);
    });
    $(document).on(
        'submit',
        'form[action*="/Identity/Account/Logout"], form[data-logout="true"]',
        () => {
            eventBus.emit(EVENTS.USER_LOGGED_OUT);
        }
    );
}

/**
 * Initialize application with provided modules
 * @param {Object} modules - Object containing all required modules
 */
async function initializeApplication(modules) {
    try {
        // Initialize toastr configuration
        modules.ToastrConfig.initializeToastr();

        // Wire global 401 handler for AJAX requests (session expiration)
        modules.Result.wireGlobalAjaxUnauthorizedHandler();

        // Ensure jQuery AJAX sends antiforgery header by default when token available
        modules.AntiForgery.wireJqueryAjaxDefaults();

        // Initialize sidebar observer
        modules.Sidebar.setupSidebarObserver();

        // Pre-load and apply filter values from session storage BEFORE Kendo UI initialization
        // This ensures values are applied to form elements before grid reads them
        preloadFilterValues();

        // Initialize Kendo UI with Czech culture
        modules.KendoConfig.initializeKendo();

        // Initialize Bootstrap tooltips globally
        modules.Bootstrap.initializeBootstrapTooltips();

        // Initialize app-level maximize toggle (data-maximize-toggle / data-maximize-content)
        initMaximize();

        // Setup event listeners for application lifecycle
        await setupApplicationEventListeners();

        // Emit application ready event
        eventBus.emit(EVENTS.APP_READY);
    } catch (error) {
        errorHandler.handleError(error, { context: 'Application initialization' });
    }
}

/**
 * Expose modules globally for development
 * @param {Object} modules - Object containing all required modules
 */
function exposeModulesGlobally(modules) {
    // Expose helpers globally for easy use in views without SiteModules prefix
    window.Result = modules.Result;
    window.AntiForgery = modules.AntiForgery;
    window.Files = modules.Files;
    window.KendoHelpers = modules.KendoHelpers;
    window.GridManagerAPI = modules.GridManagerAPI;
    window.CONFIG = CONFIG;
    window.eventBus = eventBus;
    window.EVENTS = EVENTS;

    // Expose serialize function globally for Kendo Grid Data callbacks
    window.AntiForgerySerialize = modules.AntiForgery.serialize;

    // Expose one-line error handling functions globally
    window.logError = logError;
    window.logWarning = logWarning;
    window.logInfo = logInfo;
    window.logDebug = logDebug;
    window.logEmoji = logEmoji;
    window.log = log;
    window.safeExecute = safeExecute;
    window.safeExecuteAsync = safeExecuteAsync;
    window.safeDOM = safeDOM;
    window.safeAjax = safeAjax;

    // Expose confirmation dialog globally for use in standalone page scripts
    window.showConfirmationDialog = showConfirmationDialog;
    window.showWarningConfirmationDialog = showWarningConfirmationDialog;

    // Development helpers (only in development)
    if (ENV.isDevelopment()) {
        window.SiteModules = {
            // Core modules
            CONFIG,
            eventBus,
            errorHandler,

            // Application modules
            ToastrConfig: modules.ToastrConfig,
            Sidebar: modules.Sidebar,
            Cookies: modules.Cookies,
            KendoConfig: modules.KendoConfig,
            KendoHelpers: modules.KendoHelpers,
            Result: modules.Result,
            AntiForgery: modules.AntiForgery,
            Files: modules.Files,
            FilterComponents: modules.FilterComponents,
            GridManager: modules.GridManager,
            GridManagerAPI: modules.GridManagerAPI,
            Bootstrap: modules.Bootstrap
        };

        // Add development helpers
        window.SiteModules.debug = {
            getConfig: path => CONFIG[path] || 'Config path not found',
            emitEvent: (event, data) => eventBus.emit(event, data),
            getErrorCount: () => errorHandler.getErrorCount(),
            resetErrors: () => errorHandler.resetErrorCount(),
            getFilterValues: () => {
                if (window.getFilterValues) {
                    return window.getFilterValues();
                } else {
                    return null;
                }
            }
        };
    }
}

// Expose modules globally
exposeModulesGlobally(modules);

/**
 * Application initialization
 * Runs when the DOM is fully loaded and ready
 */
document.addEventListener('DOMContentLoaded', () => {
    initializeApplication(modules);
});
